// Device commits and server acknowledgements of the existing meeting recorder.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  cloud: 'Saved to Chat: {seconds} s of audio.',
  pending: 'Saving recent audio on this device. Keep this page open.',
  stored: 'Audio waiting to upload is stored on this device.',
  memory: 'This browser keeps waiting audio only in this open page.',
  failed: 'Device storage failed. Recent audio may exist only in this open page.',
  low: 'Device storage may not hold a full offline recording. Keep uploads connected or free space.',
  unknown: 'Device storage capacity could not be checked.',
  offline: 'Offline. Audio is waiting to upload. Keep this page open.',
  recoveryTail: 'Audio that had not reached device storage or Chat may be missing.',
  incomplete: 'The recording is not fully saved. Some audio has not been acknowledged.',
  cloudUnknown: 'Saved audio duration is unavailable after recovery.',
};
const keys = Object.keys(en) as (keyof typeof en)[];
const translations = {
  bg: [
    'Запазено в Chat: {seconds} с аудио.',
    'Запазва се скорошното аудио на устройството. Оставете страницата отворена.',
    'Аудиото, чакащо качване, е запазено на устройството.',
    'Браузърът пази чакащото аудио само в тази отворена страница.',
    'Запазването на устройството не успя. Скорошното аудио може да е само в отворената страница.',
    'Мястото може да не стигне за целия запис без интернет. Поддържайте връзката или освободете място.',
    'Капацитетът за съхранение не може да се провери.',
    'Няма връзка. Аудиото чака качване. Оставете страницата отворена.',
    'Аудиото, което не е достигнало устройството или Chat, може да липсва.',
  ],
  hr: [
    'Spremljeno u Chat: {seconds} s zvuka.',
    'Nedavni zvuk sprema se na uređaj. Ostavite stranicu otvorenu.',
    'Zvuk koji čeka prijenos spremljen je na uređaju.',
    'Preglednik čuva zvuk na čekanju samo na ovoj otvorenoj stranici.',
    'Spremanje na uređaj nije uspjelo. Nedavni zvuk možda postoji samo na otvorenoj stranici.',
    'Prostor možda nije dovoljan za cijelu izvanmrežnu snimku. Ostanite povezani ili oslobodite prostor.',
    'Kapacitet pohrane nije se mogao provjeriti.',
    'Izvan mreže. Zvuk čeka prijenos. Ostavite stranicu otvorenu.',
    'Zvuk koji nije stigao u pohranu uređaja ili Chat možda nedostaje.',
  ],
  cs: [
    'Uloženo do Chatu: {seconds} s zvuku.',
    'Nedávný zvuk se ukládá na zařízení. Nechte stránku otevřenou.',
    'Zvuk čekající na nahrání je uložen na zařízení.',
    'Prohlížeč uchovává čekající zvuk jen na této otevřené stránce.',
    'Uložení na zařízení selhalo. Nedávný zvuk může být jen na otevřené stránce.',
    'Úložiště nemusí stačit na celý záznam bez připojení. Zůstaňte připojeni nebo uvolněte místo.',
    'Kapacitu úložiště se nepodařilo ověřit.',
    'Bez připojení. Zvuk čeká na nahrání. Nechte stránku otevřenou.',
    'Zvuk, který se nedostal do úložiště zařízení nebo Chatu, může chybět.',
  ],
  da: [
    'Gemt i Chat: {seconds} s lyd.',
    'Den seneste lyd gemmes på enheden. Hold siden åben.',
    'Lyd, der venter på upload, er gemt på enheden.',
    'Browseren gemmer kun ventende lyd på denne åbne side.',
    'Lagring på enheden mislykkedes. Den seneste lyd findes måske kun på den åbne side.',
    'Lagerpladsen rækker måske ikke til en hel offlineoptagelse. Bevar forbindelsen eller frigør plads.',
    'Lagerkapaciteten kunne ikke kontrolleres.',
    'Offline. Lyden venter på upload. Hold siden åben.',
    'Lyd, der ikke nåede enhedens lager eller Chat, kan mangle.',
  ],
  nl: [
    'Opgeslagen in Chat: {seconds} s audio.',
    'Recente audio wordt op dit apparaat opgeslagen. Houd de pagina open.',
    'Audio die op upload wacht, is op dit apparaat opgeslagen.',
    'Deze browser bewaart wachtende audio alleen op deze geopende pagina.',
    'Opslaan op het apparaat is mislukt. Recente audio staat mogelijk alleen op deze geopende pagina.',
    'De opslag is mogelijk te klein voor een volledige offlineopname. Blijf verbonden of maak ruimte vrij.',
    'De opslagcapaciteit kon niet worden gecontroleerd.',
    'Offline. Audio wacht op upload. Houd de pagina open.',
    'Audio die de apparaatopslag of Chat niet heeft bereikt, kan ontbreken.',
  ],
  et: [
    'Chati salvestatud: {seconds} s heli.',
    'Viimast heli salvestatakse seadmesse. Hoia leht avatuna.',
    'Üleslaadimist ootav heli on seadmesse salvestatud.',
    'Brauser hoiab ootel heli ainult sellel avatud lehel.',
    'Seadmesse salvestamine ebaõnnestus. Viimane heli võib olla ainult avatud lehel.',
    'Salvestusruumi ei pruugi piisata kogu võrguühenduseta salvestuseks. Hoia ühendus või vabasta ruumi.',
    'Salvestusmahtu ei saanud kontrollida.',
    'Võrguühendus puudub. Heli ootab üleslaadimist. Hoia leht avatuna.',
    'Heli, mis ei jõudnud seadme salvestusruumi või Chati, võib puududa.',
  ],
  fi: [
    'Tallennettu Chatiin: {seconds} s ääntä.',
    'Viimeisintä ääntä tallennetaan laitteelle. Pidä sivu auki.',
    'Latausta odottava ääni on tallennettu laitteelle.',
    'Selain säilyttää odottavan äänen vain tällä avoimella sivulla.',
    'Laitteen tallennus epäonnistui. Viimeisin ääni saattaa olla vain avoimella sivulla.',
    'Tallennustila ei ehkä riitä koko offline-tallenteelle. Pysy yhteydessä tai vapauta tilaa.',
    'Tallennuskapasiteettia ei voitu tarkistaa.',
    'Ei verkkoyhteyttä. Ääni odottaa latausta. Pidä sivu auki.',
    'Ääni, joka ei päätynyt laitteen tallennustilaan tai Chatiin, saattaa puuttua.',
  ],
  fr: [
    'Enregistré dans Chat : {seconds} s audio.',
    'Enregistrement audio récent sur cet appareil. Gardez cette page ouverte.',
    'Le son en attente de transfert est enregistré sur cet appareil.',
    'Ce navigateur conserve le son en attente uniquement dans cette page ouverte.',
    'Le stockage sur cet appareil a échoué. Le son récent peut exister uniquement dans cette page ouverte.',
    'Le stockage peut être insuffisant pour un enregistrement complet hors ligne. Restez connecté ou libérez de la place.',
    'La capacité de stockage n’a pas pu être vérifiée.',
    'Hors ligne. Le son attend son transfert. Gardez cette page ouverte.',
    'Le son qui n’a pas atteint le stockage de l’appareil ou Chat peut manquer.',
  ],
  de: [
    'In Chat gespeichert: {seconds} s Audio.',
    'Aktuelles Audio wird auf diesem Gerät gespeichert. Lassen Sie diese Seite geöffnet.',
    'Audio, das auf den Upload wartet, ist auf diesem Gerät gespeichert.',
    'Dieser Browser behält wartendes Audio nur auf dieser geöffneten Seite.',
    'Das Speichern auf dem Gerät ist fehlgeschlagen. Aktuelles Audio liegt möglicherweise nur auf dieser geöffneten Seite.',
    'Der Gerätespeicher reicht möglicherweise nicht für eine vollständige Offline-Aufnahme. Lassen Sie die Uploads verbunden oder geben Sie Speicherplatz frei.',
    'Die Speicherkapazität konnte nicht geprüft werden.',
    'Offline. Audio wartet auf den Upload. Lassen Sie diese Seite geöffnet.',
    'Audio, das den Gerätespeicher oder Chat nicht erreicht hat, fehlt möglicherweise.',
  ],
  el: [
    'Αποθηκεύτηκαν στο Chat: {seconds} δ. ήχου.',
    'Ο πρόσφατος ήχος αποθηκεύεται στη συσκευή. Κρατήστε τη σελίδα ανοιχτή.',
    'Ο ήχος που περιμένει μεταφόρτωση έχει αποθηκευτεί στη συσκευή.',
    'Το πρόγραμμα περιήγησης κρατά τον ήχο σε αναμονή μόνο σε αυτή την ανοιχτή σελίδα.',
    'Η αποθήκευση στη συσκευή απέτυχε. Ο πρόσφατος ήχος μπορεί να υπάρχει μόνο στην ανοιχτή σελίδα.',
    'Ο χώρος ίσως δεν αρκεί για ολόκληρη εγγραφή εκτός σύνδεσης. Μείνετε συνδεδεμένοι ή ελευθερώστε χώρο.',
    'Η χωρητικότητα αποθήκευσης δεν μπόρεσε να ελεγχθεί.',
    'Εκτός σύνδεσης. Ο ήχος περιμένει μεταφόρτωση. Κρατήστε τη σελίδα ανοιχτή.',
    'Ήχος που δεν έφτασε στον χώρο της συσκευής ή στο Chat μπορεί να λείπει.',
  ],
  hu: [
    'A Chatbe mentve: {seconds} mp hang.',
    'A legutóbbi hang mentése az eszközre. Tartsd nyitva az oldalt.',
    'A feltöltésre váró hang az eszközre van mentve.',
    'A böngésző csak ezen a nyitott oldalon őrzi a várakozó hangot.',
    'Az eszközre mentés sikertelen. A legutóbbi hang csak a nyitott oldalon lehet meg.',
    'A tárhely nem biztos, hogy elegendő a teljes offline felvételhez. Maradj kapcsolatban vagy szabadíts fel helyet.',
    'A tárhely kapacitása nem ellenőrizhető.',
    'Nincs kapcsolat. A hang feltöltésre vár. Tartsd nyitva az oldalt.',
    'Az eszköz tárhelyére vagy a Chatbe el nem jutott hang hiányozhat.',
  ],
  ga: [
    'Sábháilte in Chat: {seconds} s fuaime.',
    'Tá an fhuaim is déanaí á sábháil ar an ngléas. Coinnigh an leathanach ar oscailt.',
    'Tá an fhuaim atá ag fanacht le huaslódáil sábháilte ar an ngléas.',
    'Ní choinníonn an brabhsálaí an fhuaim atá ag fanacht ach sa leathanach oscailte seo.',
    'Theip ar stóráil an ghléis. B’fhéidir nach bhfuil an fhuaim is déanaí ach sa leathanach oscailte.',
    'Seans nach leor an stóráil do thaifeadadh iomlán as líne. Fan ceangailte nó saor spás.',
    'Níorbh fhéidir an toilleadh stórála a sheiceáil.',
    'As líne. Tá an fhuaim ag fanacht le huaslódáil. Coinnigh an leathanach ar oscailt.',
    'D’fhéadfadh fuaim nár shroich stóráil an ghléis ná Chat a bheith ar iarraidh.',
  ],
  it: [
    'Salvato in Chat: {seconds} s di audio.',
    'Salvataggio dell’audio recente sul dispositivo. Tieni aperta questa pagina.',
    'L’audio in attesa di caricamento è salvato sul dispositivo.',
    'Il browser conserva l’audio in attesa solo in questa pagina aperta.',
    'Il salvataggio sul dispositivo non è riuscito. L’audio recente potrebbe esistere solo nella pagina aperta.',
    'Lo spazio potrebbe non bastare per una registrazione offline completa. Resta connesso o libera spazio.',
    'Non è stato possibile verificare la capacità di archiviazione.',
    'Offline. L’audio attende il caricamento. Tieni aperta questa pagina.',
    'L’audio che non ha raggiunto il dispositivo o Chat potrebbe mancare.',
  ],
  lv: [
    'Saglabāts Chat: {seconds} s audio.',
    'Nesenais audio tiek saglabāts ierīcē. Turiet lapu atvērtu.',
    'Augšupielādi gaidošais audio ir saglabāts ierīcē.',
    'Pārlūks gaidošo audio glabā tikai šajā atvērtajā lapā.',
    'Saglabāšana ierīcē neizdevās. Nesenais audio var būt tikai atvērtajā lapā.',
    'Vietas var nepietikt pilnam bezsaistes ierakstam. Saglabājiet savienojumu vai atbrīvojiet vietu.',
    'Krātuves ietilpību nevarēja pārbaudīt.',
    'Bezsaistē. Audio gaida augšupielādi. Turiet lapu atvērtu.',
    'Audio, kas nav sasniedzis ierīces krātuvi vai Chat, var trūkt.',
  ],
  lt: [
    'Išsaugota Chat: {seconds} s garso.',
    'Naujausias garsas saugomas įrenginyje. Palikite puslapį atidarytą.',
    'Įkėlimo laukiantis garsas išsaugotas įrenginyje.',
    'Naršyklė laukiantį garsą laiko tik šiame atidarytame puslapyje.',
    'Išsaugoti įrenginyje nepavyko. Naujausias garsas gali būti tik atidarytame puslapyje.',
    'Vietos gali neužtekti visam įrašui neprisijungus. Likite prisijungę arba atlaisvinkite vietos.',
    'Saugyklos talpos patikrinti nepavyko.',
    'Neprisijungta. Garsas laukia įkėlimo. Palikite puslapį atidarytą.',
    'Garsas, nepasiekęs įrenginio saugyklos ar Chat, gali būti prarastas.',
  ],
  mt: [
    'Issejvjat f’Chat: {seconds} s ta’ awdjo.',
    'L-awdjo riċenti qed jiġi ssejvjat fuq it-tagħmir. Żomm din il-paġna miftuħa.',
    'L-awdjo li qed jistenna t-tlugħ hu ssejvjat fuq it-tagħmir.',
    'Dan il-browser iżomm l-awdjo li qed jistenna biss f’din il-paġna miftuħa.',
    'Il-ħażna tat-tagħmir falliet. L-awdjo riċenti jista’ jeżisti biss fil-paġna miftuħa.',
    'Il-ħażna tista’ ma tkunx biżżejjed għal reġistrazzjoni offline sħiħa. Ibqa’ konness jew ħeles spazju.',
    'Il-kapaċità tal-ħażna ma setgħetx tiġi ċċekkjata.',
    'Offline. L-awdjo qed jistenna t-tlugħ. Żomm din il-paġna miftuħa.',
    'Awdjo li ma wasalx fil-ħażna tat-tagħmir jew f’Chat jista’ jkun nieqes.',
  ],
  pl: [
    'Zapisano w Chat: {seconds} s dźwięku.',
    'Najnowszy dźwięk jest zapisywany na urządzeniu. Nie zamykaj tej strony.',
    'Dźwięk oczekujący na przesłanie jest zapisany na urządzeniu.',
    'Przeglądarka przechowuje oczekujący dźwięk tylko na tej otwartej stronie.',
    'Zapis na urządzeniu nie powiódł się. Najnowszy dźwięk może istnieć tylko na otwartej stronie.',
    'Pamięci może nie wystarczyć na całe nagranie offline. Zachowaj połączenie lub zwolnij miejsce.',
    'Nie można sprawdzić pojemności pamięci.',
    'Offline. Dźwięk czeka na przesłanie. Nie zamykaj tej strony.',
    'Dźwięku, który nie trafił do pamięci urządzenia lub Chat, może brakować.',
  ],
  pt: [
    'Guardado no Chat: {seconds} s de áudio.',
    'A guardar áudio recente neste dispositivo. Mantenha esta página aberta.',
    'O áudio que aguarda envio está guardado neste dispositivo.',
    'Este navegador mantém o áudio pendente apenas nesta página aberta.',
    'O armazenamento do dispositivo falhou. O áudio recente pode existir apenas nesta página aberta.',
    'O espaço pode não chegar para uma gravação offline completa. Mantenha a ligação ou liberte espaço.',
    'Não foi possível verificar a capacidade de armazenamento.',
    'Offline. O áudio aguarda envio. Mantenha esta página aberta.',
    'O áudio que não chegou ao armazenamento do dispositivo ou ao Chat pode estar em falta.',
  ],
  ro: [
    'Salvat în Chat: {seconds} s de sunet.',
    'Sunetul recent se salvează pe dispozitiv. Păstrați pagina deschisă.',
    'Sunetul care așteaptă încărcarea este salvat pe dispozitiv.',
    'Browserul păstrează sunetul în așteptare doar în această pagină deschisă.',
    'Stocarea pe dispozitiv a eșuat. Sunetul recent poate exista doar în pagina deschisă.',
    'Spațiul poate fi insuficient pentru o înregistrare offline completă. Rămâneți conectați sau eliberați spațiu.',
    'Capacitatea de stocare nu a putut fi verificată.',
    'Offline. Sunetul așteaptă încărcarea. Păstrați pagina deschisă.',
    'Sunetul care nu a ajuns în stocarea dispozitivului sau în Chat poate lipsi.',
  ],
  sk: [
    'Uložené v Chate: {seconds} s zvuku.',
    'Nedávny zvuk sa ukladá do zariadenia. Nechajte stránku otvorenú.',
    'Zvuk čakajúci na nahratie je uložený v zariadení.',
    'Prehliadač uchováva čakajúci zvuk iba na tejto otvorenej stránke.',
    'Uloženie do zariadenia zlyhalo. Nedávny zvuk môže byť iba na otvorenej stránke.',
    'Úložisko nemusí stačiť na celý záznam bez pripojenia. Zostaňte pripojení alebo uvoľnite miesto.',
    'Kapacitu úložiska sa nepodarilo overiť.',
    'Bez pripojenia. Zvuk čaká na nahratie. Nechajte stránku otvorenú.',
    'Zvuk, ktorý sa nedostal do úložiska zariadenia alebo Chatu, môže chýbať.',
  ],
  sl: [
    'Shranjeno v Chat: {seconds} s zvoka.',
    'Nedavni zvok se shranjuje v napravo. Pustite stran odprto.',
    'Zvok, ki čaka na nalaganje, je shranjen v napravi.',
    'Brskalnik hrani čakajoči zvok samo na tej odprti strani.',
    'Shranjevanje v napravo ni uspelo. Nedavni zvok je morda samo na odprti strani.',
    'Prostora morda ni dovolj za celoten posnetek brez povezave. Ostanite povezani ali sprostite prostor.',
    'Zmogljivosti shranjevanja ni bilo mogoče preveriti.',
    'Brez povezave. Zvok čaka na nalaganje. Pustite stran odprto.',
    'Zvok, ki ni dosegel shrambe naprave ali Chata, morda manjka.',
  ],
  es: [
    'Guardado en Chat: {seconds} s de audio.',
    'Guardando audio reciente en este dispositivo. Mantén esta página abierta.',
    'El audio pendiente de subir está guardado en este dispositivo.',
    'Este navegador conserva el audio pendiente solo en esta página abierta.',
    'El almacenamiento del dispositivo falló. El audio reciente puede existir solo en esta página abierta.',
    'Puede faltar espacio para una grabación sin conexión completa. Mantén la conexión o libera espacio.',
    'No se pudo comprobar la capacidad de almacenamiento.',
    'Sin conexión. El audio espera para subir. Mantén esta página abierta.',
    'Puede faltar el audio que no llegó al almacenamiento del dispositivo o a Chat.',
  ],
  sv: [
    'Sparat i Chat: {seconds} s ljud.',
    'Sparar det senaste ljudet på enheten. Håll sidan öppen.',
    'Ljud som väntar på uppladdning är sparat på enheten.',
    'Webbläsaren behåller väntande ljud bara på den här öppna sidan.',
    'Lagringen på enheten misslyckades. Det senaste ljudet kan finnas bara på den öppna sidan.',
    'Lagringsutrymmet kanske inte räcker till hela inspelningen utan nät. Behåll anslutningen eller frigör utrymme.',
    'Lagringskapaciteten kunde inte kontrolleras.',
    'Offline. Ljud väntar på uppladdning. Håll sidan öppen.',
    'Ljud som inte nådde enhetens lagring eller Chat kan saknas.',
  ],
};
const incomplete = {
  bg: 'Записът не е напълно запазен. Част от аудиото не е потвърдено.',
  hr: 'Snimka nije potpuno spremljena. Dio zvuka nije potvrđen.',
  cs: 'Záznam není celý uložený. Část zvuku nebyla potvrzena.',
  da: 'Optagelsen er ikke helt gemt. Noget lyd er ikke bekræftet.',
  nl: 'De opname is niet volledig opgeslagen. Een deel van de audio is niet bevestigd.',
  et: 'Salvestis pole täielikult salvestatud. Osa helist pole kinnitatud.',
  fi: 'Tallennetta ei ole kokonaan tallennettu. Osa äänestä on vahvistamatta.',
  fr: 'L’enregistrement n’est pas entièrement sauvegardé. Une partie du son n’a pas été confirmée.',
  de: 'Die Aufnahme ist nicht vollständig gespeichert. Ein Teil des Audios wurde nicht bestätigt.',
  el: 'Η εγγραφή δεν έχει αποθηκευτεί πλήρως. Μέρος του ήχου δεν έχει επιβεβαιωθεί.',
  hu: 'A felvétel nincs teljesen elmentve. A hang egy része nincs visszaigazolva.',
  ga: 'Níl an taifeadadh sábháilte go hiomlán. Níl cuid den fhuaim deimhnithe.',
  it: 'La registrazione non è completamente salvata. Parte dell’audio non è stata confermata.',
  lv: 'Ieraksts nav pilnībā saglabāts. Daļa audio nav apstiprināta.',
  lt: 'Įrašas nėra visiškai išsaugotas. Dalis garso nepatvirtinta.',
  mt: 'Ir-reġistrazzjoni mhix issejvjata kollha. Xi awdjo ma ġiex ikkonfermat.',
  pl: 'Nagranie nie jest w pełni zapisane. Część dźwięku nie została potwierdzona.',
  pt: 'A gravação não está totalmente guardada. Parte do áudio não foi confirmada.',
  ro: 'Înregistrarea nu este complet salvată. O parte din sunet nu a fost confirmată.',
  sk: 'Záznam nie je celý uložený. Časť zvuku nebola potvrdená.',
  sl: 'Posnetek ni v celoti shranjen. Del zvoka ni bil potrjen.',
  es: 'La grabación no está completamente guardada. Parte del audio no se ha confirmado.',
  sv: 'Inspelningen är inte helt sparad. En del av ljudet är inte bekräftat.',
};
const cloudUnknown = {
  bg: 'Продължителността на запазеното аудио не е налична след възстановяване.',
  hr: 'Trajanje spremljenog zvuka nije dostupno nakon oporavka.',
  cs: 'Délka uloženého zvuku není po obnovení dostupná.',
  da: 'Varigheden af den gemte lyd er ikke tilgængelig efter gendannelse.',
  nl: 'De duur van opgeslagen audio is na herstel niet beschikbaar.',
  et: 'Salvestatud heli kestus ei ole pärast taastamist saadaval.',
  fi: 'Tallennetun äänen kesto ei ole saatavilla palautuksen jälkeen.',
  fr: 'La durée du son enregistré est indisponible après récupération.',
  de: 'Die Dauer des gespeicherten Audios ist nach der Wiederherstellung nicht verfügbar.',
  el: 'Η διάρκεια του αποθηκευμένου ήχου δεν είναι διαθέσιμη μετά την ανάκτηση.',
  hu: 'A mentett hang időtartama a helyreállítás után nem érhető el.',
  ga: 'Níl fad na fuaime sábháilte ar fáil tar éis aisghabhála.',
  it: 'La durata dell’audio salvato non è disponibile dopo il recupero.',
  lv: 'Saglabātā audio ilgums pēc atkopšanas nav pieejams.',
  lt: 'Išsaugoto garso trukmė po atkūrimo nepasiekiama.',
  mt: 'It-tul tal-awdjo ssejvjat mhux disponibbli wara l-irkupru.',
  pl: 'Czas trwania zapisanego dźwięku jest niedostępny po odzyskaniu.',
  pt: 'A duração do áudio guardado não está disponível após a recuperação.',
  ro: 'Durata sunetului salvat nu este disponibilă după recuperare.',
  sk: 'Dĺžka uloženého zvuku nie je po obnovení dostupná.',
  sl: 'Trajanje shranjenega zvoka po obnovitvi ni na voljo.',
  es: 'La duración del audio guardado no está disponible tras la recuperación.',
  sv: 'Längden på sparat ljud är inte tillgänglig efter återställning.',
};
for (const locale of Object.keys(translations) as (keyof typeof translations)[])
  translations[locale].push(incomplete[locale], cloudUnknown[locale]);
const catalog = Object.fromEntries(
  Object.entries(translations).map(([locale, values]) => [
    locale,
    Object.fromEntries(keys.map((key, i) => [key, values[i]])),
  ]),
) as NamespaceCatalog<keyof typeof en>;
catalog.en = en;
export default catalog;
